import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { workspaceService, topicService, promptService, tagService } from "../lib/services";
import type {
  WorkspaceWithMembership,
  Topic,
  Prompt,
  PromptDetail,
  Tag,
} from "../lib/types";

// ============================================================================
// Generic fetch hook
// ============================================================================

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

function useApiQuery<T>(
  fetcher: (() => Promise<T>) | null,
  deps: unknown[] = [],
): UseApiState<T> & { refetch: () => void } {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: !!fetcher,
    error: null,
  });
  const mountedRef = useRef(true);

  const fetch = useCallback(() => {
    if (!fetcher) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    fetcher()
      .then((data) => {
        if (mountedRef.current) setState({ data, loading: false, error: null });
      })
      .catch((err: Error) => {
        if (mountedRef.current) setState({ data: null, loading: false, error: err.message });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    mountedRef.current = true;
    fetch();
    return () => {
      mountedRef.current = false;
    };
  }, [fetch]);

  return { ...state, refetch: fetch };
}

// ============================================================================
// Resource hooks
// ============================================================================

export function useWorkspaces() {
  const { session } = useAuth();
  return useApiQuery<WorkspaceWithMembership[]>(
    session ? () => workspaceService.list() : null,
    [session?.access_token],
  );
}

export function useTopics(workspaceId: string | null) {
  return useApiQuery<Topic[]>(
    workspaceId ? () => topicService.list(workspaceId) : null,
    [workspaceId],
  );
}

export function usePrompts(
  workspaceId: string | null,
  params?: { search?: string; topicId?: string; page?: number; limit?: number },
) {
  return useApiQuery<Prompt[]>(
    workspaceId ? () => promptService.list(workspaceId, params) : null,
    [workspaceId, params?.search, params?.topicId, params?.page, params?.limit],
  );
}

export function usePrompt(workspaceId: string | null, promptId: string | null) {
  return useApiQuery<PromptDetail>(
    workspaceId && promptId
      ? () => promptService.get(workspaceId, promptId)
      : null,
    [workspaceId, promptId],
  );
}

export function useTags(workspaceId: string | null) {
  return useApiQuery<Tag[]>(
    workspaceId ? () => tagService.list(workspaceId) : null,
    [workspaceId],
  );
}
